import React, {
  useState, useCallback, useEffect
} from 'react';
import classNames from 'classnames';
import { Trans } from 'react-i18next';
import { usePhoneVerification } from '@app/hooks/usePhoneVerification';
import { useCountdownTimer } from '@app/hooks/useCountdownTimer';
import { SvgIcon } from '@common/sprites';
import { formatSecondsToMinutesSeconds } from '@common/formatSecondsToMinutesSeconds';
import { getAppName } from '@common/config/utils';
import parsePhoneNumber from 'libphonenumber-js';
import PhoneInputField from '../../shared/phone_input_field/phone_input_field';
import classes from '../classes.module.scss';
import { appLinks } from '../../../config/const';
import withPGTranslation from '../../../config/withPGTranslation';

interface PhoneEntryProps {
  onPhoneSubmit: (phoneNumber: string, agreedToTerms: boolean, consentToMarketingSms: boolean) => void;
  onBack: () => void;
  t: (key: string) => string;
  clearError: () => void;
  loading?: boolean;
  error?: {
    phoneNumber?: string[];
    general?: string[];
  };
  smsSettings: {
    allowedSmsCountries: string[];
    defaultCountry: string;
  };
  savedPhoneNumber?: string | null;
  savedAgreedToTerms?: boolean;
  savedConsentToMarketingSms: boolean;
  onTimerEndsAtChange?: (timerEndsAt: string | null) => void;
  savedTimerEndsAt?: string | null;
  smsAttempts?: number;
  incrementSmsAttempts?: () => number;
}

/**
 * PhoneEntry component - Screen for entering phone number
 * Validates phone number and sends SMS verification code
 */
function PhoneEntry({
  onPhoneSubmit,
  t,
  clearError,
  loading: parentLoading = false,
  error: parentError,
  smsSettings,
  savedPhoneNumber,
  savedAgreedToTerms = false,
  savedConsentToMarketingSms = false,
  onTimerEndsAtChange,
  savedTimerEndsAt,
  smsAttempts = 0,
  incrementSmsAttempts
}: PhoneEntryProps) {
  // Initialize phone number from saved value if available
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber || '');
  const [consentToMarketingSms, setConsentToMarketingSms] = useState(savedConsentToMarketingSms);
  const [agreedToTerms, setAgreedToTerms] = useState(savedAgreedToTerms);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Update phone number when savedPhoneNumber prop changes
  // This ensures the phone number is restored when user navigates back
  useEffect(() => {
    if (savedPhoneNumber !== undefined) {
      setPhoneNumber(savedPhoneNumber || '');
    }
  }, [savedPhoneNumber]);

  // Update agreedToTerms when savedAgreedToTerms prop changes
  // This ensures the checkbox state is restored when user navigates back
  useEffect(() => {
    if (savedAgreedToTerms !== undefined) {
      setAgreedToTerms(savedAgreedToTerms);
    }
  }, [savedAgreedToTerms]);

  useEffect(() => {
    if (consentToMarketingSms !== undefined) {
      setConsentToMarketingSms(consentToMarketingSms);
    }
  }, [consentToMarketingSms]);

  const {
    sendSmsCode, loading, error, success, timerEndsAt
  } = usePhoneVerification({
    clickSource: 'signup',
    smsAttempts,
    incrementSmsAttempts
  });

  // Use saved timerEndsAt from parent or current from hook
  // Priority: timerEndsAt from hook (from error response) > savedTimerEndsAt from parent
  const currentTimerEndsAt = timerEndsAt || savedTimerEndsAt;

  // Notify parent when timerEndsAt changes (from hook or error)
  useEffect(() => {
    if (timerEndsAt && onTimerEndsAtChange) {
      onTimerEndsAtChange(timerEndsAt);
    }
  }, [timerEndsAt, onTimerEndsAtChange]);

  // Use countdown timer based on timer_ends_at from API
  const resendCooldown = useCountdownTimer(currentTimerEndsAt);

  // Handle phone number change
  const handlePhoneChange = useCallback((value: string) => {
    setPhoneNumber(value);
    setPhoneError(null);
    if (parentError) {
      clearError();
    }
  }, [parentError, clearError]);

  // Handle form submit
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const parsedPhoneNumber = parsePhoneNumber(`+${ phoneNumber }`);
    if (parsedPhoneNumber && !parsedPhoneNumber?.isValid()) {
      setPhoneError(t('smsVerification.invalid_phone_number'));
      return;
    }

    if (parsedPhoneNumber && !parsedPhoneNumber?.isValid()) {
      setPhoneError(t('smsVerification.invalid_phone_number'));
      return;
    }

    if (loading || parentLoading) return;

    // Check if user agreed to terms
    if (!agreedToTerms) {
      return; // PhoneInputField will show validation error if phone is invalid
    }

    // Validate phone number (basic check - PhoneInputField handles detailed validation)
    if (!phoneNumber || phoneNumber.length < 8) {
      return; // PhoneInputField will show error
    }

    try {
      // react-phone-input-2 provides phone number in international format (e.g., +1234567890)
      // The value already includes country code, so we can use it directly
      await sendSmsCode(phoneNumber);
    } catch (err: any) {
      // Handle 422 error with timer_ends_at from error response
      // Note: timerEndsAt should already be set in hook, but we ensure parent is notified
      if (err?.status === 422 && err?.errors) {
        const timerEndsAtFromError = err.errors.timerEndsAt?.[0] || err.errors.timer_ends_at?.[0];
        if (timerEndsAtFromError && onTimerEndsAtChange) {
          // Notify parent immediately to save timer
          onTimerEndsAtChange(timerEndsAtFromError);
        }
      }
      // Error is handled in the hook
    }
  }, [phoneNumber, agreedToTerms, consentToMarketingSms, loading, parentLoading, sendSmsCode, onTimerEndsAtChange]);

  // Navigate to verification screen on success
  useEffect(() => {
    if (success) {
      // react-phone-input-2 provides phone number in international format
      // Don't reset timerEndsAt - it needs to persist for countdown timer
      // Pass both phoneNumber and agreedToTerms to save checkbox state
      onPhoneSubmit(phoneNumber, agreedToTerms, consentToMarketingSms);
      // Reset only error/success states, but keep timerEndsAt for countdown
      // Note: timerEndsAt will be available in phone_verification component
      // through the same hook instance or we need to pass it via props
    }
  }, [success, phoneNumber, agreedToTerms, onPhoneSubmit]);

  // Get error message - error is now a string from usePhoneVerification hook
  const parentGeneralError = parentError?.general && parentError.general[0];
  const displayError = error || parentGeneralError || phoneError;

  const isLoading = loading || parentLoading;
  // Disable button if timer is active, loading, or validation fails
  const isButtonDisabled = isLoading || !phoneNumber || !agreedToTerms || resendCooldown > 0 || !!phoneError;

  return (
    <div className={ classes.phoneEntryContainer }>
      {/* Icon */}
      <div className={ classes.phoneLoginIconContainer }>
        <div className={ classes.phoneLoginIconCircle }>
          <SvgIcon id="verification_phone_icon" />
        </div>
      </div>
      <div className={ classes.phoneEntryContent }>
        {/* Title */}
        <div className={ classes.phoneLoginTitleContainer }>
          <h2 className={ classes.phoneLoginTitle }>
            {t('smsVerification.continue_with_phone_number')}
          </h2>
          <p className={ classes.phoneLoginSubtitle }>
            {t('smsVerification.get_exclusive_offers')}
          </p>
        </div>

        <form onSubmit={ handleSubmit } className={ classes.phoneEntryForm }>
          <PhoneInputField
            value={ phoneNumber }
            onChange={ handlePhoneChange }
            disabled={ isLoading }
            placeholder="234-5678"
            error={ displayError || undefined }
            helperText={ t('smsVerification.we_will_send_code_via_sms') }
            t={ t }
            smsSettings={ smsSettings }
          />

          {/* Terms Checkbox */}
          <div className={ classes.phoneEntryTermsCheckboxContainer }>
            <div className={ classes.phoneEntryTermsContainer }>
              <div className={ classes.phoneEntryMarketingCheckboxContainer }>
                <input
                  type="checkbox"
                  id="phoneEntrySpamCheckbox"
                  checked={ consentToMarketingSms }
                  onChange={ (e) => setConsentToMarketingSms(e.target.checked) }
                  className={ classes.phoneEntryTermsCheckbox }
                />
              </div>
              <label htmlFor="phoneEntrySpamCheckbox" className={ classes.phoneEntryTermsLabel }>
                <Trans
                  i18nKey="smsVerification.phone_entry_terms_text"
                  values={ { brandName: getAppName() } }
                />
              </label>
            </div>
            <div className={ classes.phoneEntryTermsContainer }>
              <input
                type="checkbox"
                id="phoneEntryTerms"
                checked={ agreedToTerms }
                onChange={ (e) => setAgreedToTerms(e.target.checked) }
                className={ classes.phoneEntryTermsCheckbox }
              />
              <label htmlFor="phoneEntryTerms" className={ classes.phoneEntryTermsLabel }>
                <Trans
                  i18nKey="smsVerification.phone_terms_police"
                  values={ { brandName: getAppName() } }
                  components={ {
                    termsLink: <a href={ appLinks.tos } target="_blank" rel="noopener noreferrer" className={ classes.phoneEntryTermsLink } />,
                    privacyLink: <a href={ appLinks.privacyPolicy } target="_blank" rel="noopener noreferrer" className={ classes.phoneEntryTermsLink } />
                  } }
                />
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={ classNames(
              classes.phoneEntrySubmitButton,
              isButtonDisabled ? classes.phoneEntrySubmitButtonDisabled : null
            ) }
            disabled={ isButtonDisabled }
          >
            {resendCooldown > 0
              ? `${ t('smsVerification.send_verification_code') } (${ formatSecondsToMinutesSeconds(resendCooldown) })`
              : t('smsVerification.send_verification_code')}
          </button>

        </form>
      </div>
    </div>
  );
}

export default withPGTranslation(PhoneEntry);
